/**
 * Pool Users — search + manage each customer's service period (design §11 Phase 1, screen 3). `?email=`
 * pre-fills the search box — the Dashboard's two mismatch cards link here with it set, so "view in Pool
 * Users" actually lands on the row it named.
 *
 * rev 6 R5/R6: a Pool Accounts row click lands here with `?account=<account_id>` instead — an EXACT
 * server-side filter (never ILIKE), combined with `?email=`/the search box rather than replacing it. The
 * old Status column moved into PeriodModal as a read-only info row (R6); License replaces the old Type
 * header (same 2/1/null logic, just renamed to match what the pill actually shows).
 *
 * rev 9 (21/7, principal feedback):
 *  1. The 500-row ceiling was the API's own cap, now raised to 5000 (parallel BE task, same branch) — see
 *     hooks.ts's usePoolUsers / api/client.ts's POOL_USERS_REQUEST_LIMIT.
 *  2. NEW License filter (Licensed/Basic/All), reflected in `?license=` so a deep-link keeps its state.
 *     Defaults to Licensed on first entry EXCEPT when `?email=` is present at that same first entry — a
 *     dashboard mismatch card (card 2: "we have it, Zoom doesn't") can point at a customer with NO
 *     current Zoom membership at all, and a Basic-license link would suffer the exact same silent-miss
 *     bug; defaulting to All whenever `?email=` arrives pre-set means the deep-link always finds its row
 *     regardless of that customer's license state. Once the page is open, an explicit filter choice
 *     (including re-picking "Licensed") always wins and is written back to the URL.
 *  3. Row click is GONE — the Account Name cell doesn't drive it anymore either (see PoolAccountsPage for
 *     that one). Opening <PeriodModal/> is now a dedicated per-row "Set period" button, hidden entirely
 *     for a pool-Viewer (view-only doesn't get to reach a modal it can't act inside of).
 *  4. The Renew column renders EMPTY, not a pill, for anyone who isn't a CURRENT Licensed member
 *     (user_type !== 2) — a renew decision on a Basic/no-membership row is meaningless noise.
 *
 * r13 (22/7, principal feedback): point 4 above was itself wrong in the OTHER direction — gating on
 * user_type let a Licensed row with NO open period at all still show the "Not decided" pill, implying a
 * pending renew decision on a period that doesn't exist. The principal's actual question exposed this:
 * can such a user ever be license-removed? No — the expiry job scans `pool.period` only, so a user with
 * no period was never at risk regardless of their Zoom license state. The Renew cell now gates on
 * `period_id` (does a period exist at all), not `user_type` — em dash (muted) when there is no period;
 * the tri-state pill (Renewing / Not renewing / Not decided) whenever one does, REGARDLESS of license —
 * a Basic row with a live period needs its renew state visible too (same reasoning as r10's Manage-button
 * exception below).
 */

import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { Search } from "lucide-react";
import {
  Button,
  EmptyState,
  Input,
  Select,
  SkeletonRows,
  StatusPill,
  Table,
  Tooltip,
  type Column,
} from "@/components/ui";
import { PeriodModal } from "@/features/pool/PeriodModal";
import { UnlinkOnlyDialog } from "@/features/pool/UnlinkOnlyDialog";
import { BulkForceBar } from "@/features/pool/BulkForceBar";
import { BulkForceDialog } from "@/features/pool/BulkForceDialog";
import { BulkSetPeriodDialog } from "@/features/pool/BulkSetPeriodDialog";
import { FreshnessBadge } from "@/components/portal/FreshnessBadge";
import { PoolErrorState } from "@/features/pool/PoolErrorState";
import {
  poolUserNeedsReview,
  poolUserSelectDisabled,
  poolUserUnlinkOnly,
  usePoolAccess,
  usePoolAccounts,
  usePoolStatus,
  usePoolUsers,
  type PoolUserLicenseFilter,
} from "@/features/pool/hooks";
import { formatDate } from "@/lib/format";
import type { PoolBulkForceAction, PoolUser } from "@/api/types";

function parseLicenseParam(v: string | null): PoolUserLicenseFilter | null {
  return v === "2" || v === "1" || v === "all" || v === "needs-review" ? v : null;
}

export function PoolUsersPage() {
  const { t } = useTranslation();
  const [params, setParams] = useSearchParams();
  const [term, setTerm] = useState(() => params.get("email") ?? "");
  const accountId = params.get("account") ?? undefined;

  // An explicit ?license= always wins; absent it the default is now "all" EVERYWHERE (rev 15,
  // 23/7): the old Licensed default hid every post-action row — a just-forced user flips to
  // Basic and silently vanished from the very screen the operator was watching, which read as
  // "the system lost my user" twice in the first live day (pill-lag 22/7 + "cr หายไป" 23/7).
  // Derived straight from `params` (like accountId above) rather than mirrored into its own
  // useState — one source of truth, no separate effect to keep it in sync.
  const license: PoolUserLicenseFilter = parseLicenseParam(params.get("license")) ?? "all";

  const usersQ = usePoolUsers(term, accountId, license);
  const accountsQ = usePoolAccounts();
  const statusQ = usePoolStatus();
  const accessQ = usePoolAccess();
  const canManage = accessQ.data?.role === "owner" || accessQ.data?.role === "admin";
  const [manageTarget, setManageTarget] = useState<PoolUser | null>(null);

  // rev 17b — the page-scoped bulk-selection Set (§2, rev17b-fe-design.md). Keyed by email, cleared
  // whenever the CONTENT of the current page changes (page/filter/search — see handleVisibleRowsChange
  // below), never persisted across pages: F6's ruling resolves the page=50-vs-cap=10 conflict by making
  // selection page-scoped everywhere, not just for select-all.
  const [selectedEmails, setSelectedEmails] = useState<Set<string>>(new Set());
  const [visibleRows, setVisibleRows] = useState<PoolUser[]>([]);
  const [bulkAction, setBulkAction] = useState<PoolBulkForceAction | null>(null);
  // The constructive Set-period action is NOT a member of the PoolBulkForceAction union (it never touches
  // /bulk-force) — its own boolean, mounting BulkSetPeriodDialog separately from BulkForceDialog.
  const [bulkSetPeriodOpen, setBulkSetPeriodOpen] = useState(false);
  // Table's onVisibleRowsChange fires with a NEW array on every parent render (its own columns prop is
  // a fresh literal each render — see PoolUsersPage's own `columns` below), not only on an actual page/
  // sort/data change. Comparing CONTENT (the joined email list) rather than array identity is what makes
  // "clear on page/filter/search change" fire exactly once per real change instead of looping.
  const visibleSigRef = useRef<string>("");

  function handleVisibleRowsChange(rows: PoolUser[]) {
    const sig = rows.map((r) => r.email).join("|");
    if (sig === visibleSigRef.current) return;
    visibleSigRef.current = sig;
    setVisibleRows(rows);
    setSelectedEmails(new Set());
  }

  function toggleOneSelected(email: string) {
    setSelectedEmails((prev) => {
      const next = new Set(prev);
      if (next.has(email)) next.delete(email);
      else next.add(email);
      return next;
    });
  }

  const selectableVisible = visibleRows.filter((r) => !poolUserSelectDisabled(r));
  const allVisibleSelected =
    selectableVisible.length > 0 && selectableVisible.every((r) => selectedEmails.has(r.email));

  function toggleSelectAllVisible() {
    setSelectedEmails(() => {
      if (allVisibleSelected) return new Set();
      return new Set(selectableVisible.map((r) => r.email));
    });
  }

  // A fresh ?email= (a new dashboard-card click while this screen is already mounted) re-seeds the box.
  useEffect(() => {
    const email = params.get("email");
    if (email) setTerm(email);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.get("email")]);

  const filterAccount = accountId
    ? accountsQ.data?.accounts.find((a) => a.account_id === accountId)
    : undefined;

  // "needs-review" (r12 22/7) rides the exact same unfiltered fetch as "all" (see hooks.ts's
  // licenseWireValue — neither ever reaches the wire) and narrows CLIENT-SIDE from the loaded rows, using
  // the one poolUserNeedsReview definition the License-cell badge above reads too.
  const displayedUsers = usersQ.data
    ? license === "needs-review"
      ? usersQ.data.users.filter(poolUserNeedsReview)
      : usersQ.data.users
    : [];

  function clearAccountFilter() {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete("account");
      return next;
    });
  }

  // Always writes the LITERAL choice, including "all" — deleting the param on "all" would make it
  // indistinguishable from "never touched yet" (which falls back to the first-entry default above) and
  // silently un-pick an explicit "All" back to Licensed the next time this derivation runs.
  function setLicenseFilter(next: PoolUserLicenseFilter) {
    setParams((prev) => {
      const nextParams = new URLSearchParams(prev);
      nextParams.set("license", next);
      return nextParams;
    });
  }

  const columns: Column<PoolUser>[] = [
    // rev 17b — the multi-select checkbox column, canManage only, ALWAYS the leading column (before
    // Email) — Pool-Viewer gets neither this column nor the bulk bar (unchanged read-only rule).
    ...(canManage
      ? [
          {
            key: "select",
            header: (
              <input
                type="checkbox"
                aria-label={t("pool.users.selectAllLabel")}
                checked={allVisibleSelected}
                onChange={toggleSelectAllVisible}
                className="size-4 accent-(--brand)"
              />
            ),
            cell: (r: PoolUser) => {
              // Selectability itself is single-sourced from poolUserSelectDisabled (hooks.ts) — the
              // exact predicate selectableVisible/toggleSelectAllVisible above already use, so a page
              // and a select-all can never disagree on which rows are pickable. ownerAdmin/hasOpenPeriod
              // stay derived locally ONLY to pick which tooltip to show — that branching has no
              // single-source risk (nothing else reads it), unlike the disabled condition itself.
              const ownerAdmin = r.role_id === "0" || r.role_id === "1";
              const hasOpenPeriod = r.period_id != null;
              const disabled = poolUserSelectDisabled(r);
              // rev 17c (คุณใหม่ 23/7, "รกเกินไป"): the always-visible Owner/Admin badge is GONE —
              // replaced by a hover tooltip on the disabled checkbox itself. A disabled <input> doesn't
              // reliably fire pointer/hover events in every browser (the element is excluded from hit-
              // testing), so the checkbox is wrapped in <Tooltip>'s own span anchor — the exact same
              // "wrap the disabled control in a span" mechanism the open-period case below already
              // relies on via its plain `title` attribute. The open-period tooltip is untouched.
              const checkbox = (
                <input
                  type="checkbox"
                  aria-label={t("pool.users.selectRowLabel", { email: r.email })}
                  checked={selectedEmails.has(r.email)}
                  disabled={disabled}
                  title={
                    !ownerAdmin && hasOpenPeriod ? t("pool.users.openPeriodTooltip") : undefined
                  }
                  onChange={() => toggleOneSelected(r.email)}
                  className="size-4 accent-(--brand)"
                />
              );
              return ownerAdmin ? (
                <Tooltip content={t("pool.users.ownerAdminTooltip")}>{checkbox}</Tooltip>
              ) : (
                checkbox
              );
            },
          } satisfies Column<PoolUser>,
        ]
      : []),
    {
      key: "email",
      header: t("pool.users.colEmail"),
      sortValue: (r) => r.email,
      cell: (r) => <span className="font-medium text-(--text)">{r.email}</span>,
    },
    {
      key: "account",
      header: t("pool.users.colAccount"),
      sortValue: (r) => r.account_name ?? r.account_number ?? "",
      cell: (r) =>
        r.account_name || r.account_number ? (
          <span className="flex flex-col leading-tight">
            <span className="font-medium text-(--text)">{r.account_name ?? "—"}</span>
            <span className="text-(length:--text-2xs) text-(--text-muted)">
              {r.account_number ?? "—"}
            </span>
          </span>
        ) : (
          "—"
        ),
    },
    {
      key: "license",
      header: t("pool.users.colLicense"),
      sortValue: (r) => r.user_type ?? -1,
      // r12 22/7 (principal feedback, prod screenshots): a paid-but-Basic row (open period, Zoom side
      // isn't Licensed — see hooks.ts's poolUserNeedsReview, the single definition this and the
      // "needs-review" filter both read) gets a warn pill NEXT TO whatever the cell already shows
      // (Basic, or "Removed"/"Not found in pool") — never REPLACING it, so the actual Zoom state
      // stays visible.
      //
      // 31/7 (คุณใหม่, real prod row jongsert+cr3@gmail.com) — GET /pool/users LEFT-JOINs membership, so
      // a user unlinked/deleted from EVERY account comes back with account_id/user_type/role_id ALL null
      // but keeps their open period + renew decision (the server never drops the row — see this file's
      // own header banner). The old fallback here ("Not found in any account", a muted span, formerly
      // pool.users.noMembership — deleted, see localeLockstep) read as a routine "no Zoom membership yet"
      // state and let a paid-and-renewing customer show a confusing green "Renewing" pill in the Renew
      // column below. Principal decision: keep the row, mark it clearly — a warn StatusPill, not a muted
      // span, and the Renew column (below) stops implying a live renewal exists for someone the account
      // no longer has. `account_id == null` IS the "not user_type 2 or 1" branch — poolUserRow (BE, and
      // the mock's mirror of it) derives account_id/user_type from the SAME membership join, so the two
      // are never null independently — this branch is that condition made explicit rather than implicit.
      // `poolUserNeedsReview` is left UNCHANGED (the needs-review FILTER still has to catch these rows) —
      // only the redundant pill next to it is suppressed, since "Removed" already says everything
      // "Should be licensed" would.
      cell: (r) => (
        <span className="flex flex-wrap items-center gap-1.5">
          {r.user_type === 2 ? (
            <StatusPill tone="ok">{t("pool.users.typeLicensed")}</StatusPill>
          ) : r.user_type === 1 ? (
            <StatusPill tone="neutral">{t("pool.users.typeBasic")}</StatusPill>
          ) : (
            <StatusPill tone="warn" title={t("pool.users.removedTooltip")}>
              {t("pool.users.removedBadge")}
            </StatusPill>
          )}
          {r.account_id != null && poolUserNeedsReview(r) && (
            <StatusPill tone="warn" title={t("pool.users.needsReviewTooltip")}>
              {t("pool.users.needsReviewBadge")}
            </StatusPill>
          )}
        </span>
      ),
    },
    {
      key: "serviceStart",
      header: t("pool.users.colServiceStart"),
      sortValue: (r) => r.period_start ?? "",
      cell: (r) => (
        <span className="tabular text-(length:--text-sm)">{formatDate(r.period_start)}</span>
      ),
    },
    {
      key: "serviceEnd",
      header: t("pool.users.colServiceEnd"),
      sortValue: (r) => r.period_end ?? "",
      cell: (r) => (
        <span className="tabular text-(length:--text-sm)">{formatDate(r.period_end)}</span>
      ),
    },
    {
      key: "renew",
      header: t("pool.users.colRenew"),
      // r13 (22/7): gated on whether a period EXISTS (period_id), not on user_type — see this file's own
      // r13 banner. A row with no period has no meaningful renew state — nulls sort last regardless of
      // direction (Table's own "nulls last" rule), which reads correctly here.
      //
      // 31/7 — ALSO gated on account_id (the "Removed" condition, see the License cell's own comment
      // above): the stored renew decision is real and stays visible inside Manage, but a "Renewing"
      // pill here — with no owning account left — is exactly the confusing signal the principal flagged
      // on jongsert+cr3@gmail.com. Same dash as the no-period case, including no "Next: …" subline.
      sortValue: (r) =>
        r.account_id == null || r.period_id == null
          ? null
          : r.renew === true
            ? 2
            : r.renew === false
              ? 0
              : 1,
      cell: (r) =>
        r.account_id == null || r.period_id == null ? (
          <span className="text-(--text-muted)">—</span>
        ) : (
          <div className="flex flex-col items-start gap-1">
            <StatusPill tone={r.renew === true ? "ok" : r.renew === false ? "warn" : "neutral"}>
              {r.renew === true
                ? t("pool.users.renewYes")
                : r.renew === false
                  ? t("pool.users.renewNo")
                  : t("pool.users.renewUndecided")}
            </StatusPill>
            {/* 30/7 — a "Renewing" pill on its own no longer says WHEN, because the Service period
                column keeps showing the CURRENT round until the switch-over date (that is the fix for
                คุณใหม่'s bug 2). Without this line the planned dates would be invisible unless you
                opened the modal. */}
            {r.next_period_start && r.next_period_end && (
              <span className="tabular text-(length:--text-2xs) text-(--text-subtle)">
                {t("pool.period.nextRoundPlanned", {
                  start: formatDate(r.next_period_start),
                  end: formatDate(r.next_period_end),
                })}
              </span>
            )}
          </div>
        ),
    },
    ...(canManage
      ? [
          {
            key: "actions",
            header: "",
            align: "right" as const,
            // rev 17b (Manage-every-row matrix, rev17b-addendum.md): Manage now renders on EVERY row
            // when canManage — the r10 22/7 "hide it for Basic/no-membership with no open period" rule
            // is GONE. That combination is no longer nothing-to-manage: `poolUserUnlinkOnly` routes it
            // to the new UnlinkOnlyDialog instead (below); PeriodModal stays the target for a Licensed
            // row or any row that still carries an open period.
            cell: (r: PoolUser) => (
              <Button size="sm" variant="secondary" onClick={() => setManageTarget(r)}>
                {t("pool.users.manageButton")}
              </Button>
            ),
          } satisfies Column<PoolUser>,
        ]
      : []),
  ];

  return (
    <section aria-labelledby="pool-users-title" className="flex flex-col gap-6">
      <header className="flex flex-col gap-1.5">
        <div className="flex flex-wrap items-center gap-3">
          <h1
            id="pool-users-title"
            className="text-(length:--text-2xl) font-semibold text-(--text-strong)"
          >
            {t("pool.users.title")}
          </h1>
          {/* Last update (คุณใหม่ 22/7): ชิปเดียวกับหน้า accounts — เวลาที่ sweep complete ล่าสุด
              คือความสดของทุกคอลัมน์ observed ในตารางนี้ */}
          {!statusQ.isPending && !statusQ.isError && (
            <FreshnessBadge
              kind="mirror"
              timestamp={statusQ.data.last_complete?.finished_at ?? null}
            />
          )}
        </div>
        <p className="text-(length:--text-sm) text-(--text-muted)">{t("pool.users.subtitle")}</p>
      </header>

      <div className="flex flex-wrap items-end gap-3">
        <div className="relative w-full max-w-sm">
          <Search
            className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-(--text-subtle)"
            aria-hidden
          />
          <Input
            type="search"
            value={term}
            onChange={(e) => setTerm(e.target.value)}
            placeholder={t("pool.users.searchPlaceholder")}
            aria-label={t("pool.users.searchLabel")}
            autoComplete="off"
            spellCheck={false}
            className="h-9 w-full pl-9"
          />
        </div>

        <label className="flex flex-col gap-1.5">
          <span className="text-(length:--text-xs) font-medium text-(--text-muted)">
            {t("pool.users.filterLicenseLabel")}
          </span>
          <Select
            value={license}
            onChange={(v) => setLicenseFilter(v as PoolUserLicenseFilter)}
            options={[
              { value: "2", label: t("pool.users.typeLicensed") },
              { value: "1", label: t("pool.users.typeBasic") },
              { value: "all", label: t("pool.users.filterAllOption") },
              // r12 22/7: needs-review is a CLIENT-SIDE narrowing of the same "all" fetch — see
              // hooks.ts's poolUserNeedsReview and this file's own displayedUsers derivation.
              { value: "needs-review", label: t("pool.users.filterNeedsReviewOption") },
            ]}
            aria-label={t("pool.users.filterLicenseLabel")}
            size="sm"
          />
        </label>
      </div>

      {accountId && (
        <div className="flex flex-wrap items-center gap-2 text-(length:--text-sm) text-(--text-muted)">
          <span>
            {t("pool.users.filteredByAccount", {
              account: filterAccount?.account_name ?? filterAccount?.account_number ?? accountId,
            })}
          </span>
          <button
            type="button"
            onClick={clearAccountFilter}
            className="rounded-(--radius-sm) px-1.5 py-0.5 text-(length:--text-2xs) font-medium text-(--text-muted) underline-offset-2 hover:text-(--text) hover:underline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-(--brand)"
          >
            {t("pool.users.clearAccountFilter")}
          </button>
        </div>
      )}

      {canManage && (
        <BulkForceBar
          count={selectedEmails.size}
          onClear={() => setSelectedEmails(new Set())}
          onSetPeriod={() => setBulkSetPeriodOpen(true)}
          onLicenseRemove={() => setBulkAction("license_remove")}
          onUnlink={() => setBulkAction("unlink")}
        />
      )}

      {usersQ.isPending ? (
        <SkeletonRows rows={6} />
      ) : usersQ.isError ? (
        <PoolErrorState error={usersQ.error} onRetry={() => void usersQ.refetch()} />
      ) : (
        <Table
          columns={columns}
          rows={displayedUsers}
          rowKey={(r) => r.email}
          caption={t("pool.users.title")}
          initialSort={{ key: "email", dir: "asc" }}
          initialPageSize={50}
          empty={<EmptyState title={t("pool.users.emptyTitle")} body={t("pool.users.emptyBody")} />}
          // Harmless when !canManage — the checkbox column/bulk bar are both gated on canManage too, so
          // this just tracks a Set nothing ever reads. Always passing it (rather than conditionally
          // `| undefined`) avoids fighting exactOptionalPropertyTypes for zero behavioural difference.
          onVisibleRowsChange={handleVisibleRowsChange}
        />
      )}

      {manageTarget &&
        (poolUserUnlinkOnly(manageTarget) ? (
          <UnlinkOnlyDialog user={manageTarget} onClose={() => setManageTarget(null)} />
        ) : (
          <PeriodModal user={manageTarget} onClose={() => setManageTarget(null)} />
        ))}

      {bulkAction && (
        <BulkForceDialog
          action={bulkAction}
          users={displayedUsers.filter((u) => selectedEmails.has(u.email))}
          onClose={() => {
            setBulkAction(null);
            setSelectedEmails(new Set());
          }}
        />
      )}

      {bulkSetPeriodOpen && (
        <BulkSetPeriodDialog
          users={displayedUsers.filter((u) => selectedEmails.has(u.email))}
          onClose={() => {
            setBulkSetPeriodOpen(false);
            setSelectedEmails(new Set());
          }}
        />
      )}
    </section>
  );
}
